import React from 'react';
import {IconButton, Stack} from "@mui/material";
import {useNavigate, useParams} from "react-router";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import {useReportStore} from "../../useReportStore";
import ReportTitle from "../../reportPage/leftPanel/header/reportTitle";
import {useSession} from "../../../../firebase/UserProvider";
import ShareDialog from './ShareDialog';
import ReportActionsMenu from './ReportActionsMenu';
import DeleteReportDialog from './DeleteReportDialog';
import ReportHeaderActions from './ReportHeaderActions';
import {useReportActions} from './useReportActions';
import {useEditorContent} from './useEditorContent';
import {useViewToggle} from './useViewToggle';

const ReportHeader = ({rteRef: _rteRef, isViewer = false, role = null}) => {
    const navigate = useNavigate();
    const {user} = useSession();
    const {reportId} = useParams();

    // Store state
    const metaFromStore = useReportStore(s => s.meta);
    const title = useReportStore(state => state.title);
    const status = useReportStore(state => state.status);
    const productType = useReportStore(state => state.productType);
    const validation = useReportStore(state => state.validation);
    const updateReport = useReportStore(state => state.updateReport);
    const setContent = useReportStore(state => state.setContent);

    // Dialog state
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [shareOpen, setShareOpen] = React.useState(false);

    // Menu state for the three-dot menu
    const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
    const menuOpen = Boolean(menuAnchorEl);

    // Custom hooks for business logic
    const {getEditorContent} = useEditorContent(_rteRef);
    const {isPreview, handleViewToggle} = useViewToggle(reportId, setContent, getEditorContent);
    const {
        handleDelete,
        handleExport,
        handleSubmitForReview,
        handleRecall,
        deleting,
        submittingReview,
        recalling
    } = useReportActions({
        reportId,
        title,
        status,
        metaFromStore,
        user,
        updateReport,
        productType
    });

    const navigateBack = () => {
        if (isPreview && !isViewer && reportId) {
            navigate(`/products/${reportId}/edit`);
        } else {
            navigate('/products');
        }
    };

    const handleDeleteConfirm = async () => {
        await handleDelete();
        setConfirmOpen(false);
    };

    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            boxSizing='border-box'
            p={1}
            py={1}
            bgcolor={'white'}
            spacing={1}
            justifyContent={'space-between'}
        >
            <Stack
                direction={'row'}
                alignItems={'center'}
                boxSizing='border-box'
                bgcolor={'white'}
                spacing={1}
                flexGrow={1}
            >
                <IconButton onClick={navigateBack}>
                    <ArrowBackOutlinedIcon/>
                </IconButton>
                <ReportTitle title={title} role={role} status={status}/>
            </Stack>

            <ReportHeaderActions
                isViewer={isViewer}
                isPreview={isPreview}
                status={status}
                reportId={reportId}
                title={title}
                validation={validation}
                submittingReview={submittingReview}
                recalling={recalling}
                onViewToggle={handleViewToggle}
                onSubmitForReview={handleSubmitForReview}
                onRecall={handleRecall}
                onMenuOpen={(e) => setMenuAnchorEl(e.currentTarget)}
                isAdmin={user?.isAdmin === true}
            />

            <ReportActionsMenu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={() => setMenuAnchorEl(null)}
                role={role}
                onExport={handleExport}
                onShareOpen={() => setShareOpen(true)}
                onDeleteOpen={() => setConfirmOpen(true)}
            />

            <DeleteReportDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                title={title}
            />

            {role === 'owner' && (
                <ShareDialog
                    open={shareOpen}
                    onClose={() => setShareOpen(false)}
                    reportId={reportId}
                />
            )}
        </Stack>
    );
};

export default ReportHeader;
